// Малките клетки на решетката „Изписване" — общи за продуктовия ред
// (WriteoffWorksheetTable) и реда-рецепта (WriteoffRecipeRow): номерът, обвивките
// на плоските полета, тирето, парите и ✕. Плоската клетка е като .dwx-cell в
// доставката: прозрачна рамка/фон, на фокус — оранжева рамка + бял фон + мек ring.
import type { ReactNode } from "react";
import { SHEET_INPUT } from "../../components/list/density";
import { formatMoney, formatUnitPrice } from "./writeoffWorksheetShared";

// leading-[normal] = line-height като от `font:` shorthand-а в доставката
// (иначе клетките стават по-високи)
export const FLAT_CELL_CLASS =
  "box-border w-full rounded-[5px] border-[1.5px] border-transparent bg-transparent leading-[normal] outline-none " +
  "focus:border-brand focus:bg-surface focus:shadow-[0_0_0_3px_rgba(217,127,0,0.15)]";

// Числото в „Количество"/„Порции" — mono, вдясно
export const QTY_INPUT_CLASS = `${FLAT_CELL_CLASS} ${SHEET_INPUT} px-[9px] text-right font-[ui-monospace,Menlo,monospace] text-[13px] font-semibold text-ink placeholder:text-faint`;
export const NOTE_INPUT_CLASS = `${FLAT_CELL_CLASS} ${SHEET_INPUT} px-[9px] text-[12.5px] text-body placeholder:text-faint`;

export function RowIndexCell({ index }: { index: number }) {
  return (
    <div className="flex items-center justify-center border-r border-shade-2 bg-fill font-[ui-monospace,Menlo,monospace] text-[11px] font-semibold text-muted">
      {index + 1}
    </div>
  );
}

// Като CellBox в доставката: block обвивка с padding 2 — полето вътре опира
// почти от край до край на клетката, а бордюрът е на самата клетка.
export function RowCell({ children }: { children: ReactNode }) {
  return <div className="border-r border-shade-2 p-[2px]">{children}</div>;
}

// Като PickCell обвивката в доставката (за клетката „Партида" със Select)
export function PickBox({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 items-stretch border-r border-shade-2 p-[2px]">{children}</div>;
}

// Затворената „Партида" — плоска клетка с височината на реда; стойността
// вътре (`<span className="truncate">`) се реже с „…"
export const PICK_CELL_CLASS = `${FLAT_CELL_CLASS} ${SHEET_INPUT} flex min-w-0 cursor-pointer items-center px-[9px] text-[13px] text-body`;

export function DashCell() {
  return (
    <div className="flex items-center justify-center border-r border-shade-2 text-[13px] text-ghost">—</div>
  );
}

export function MoneyCell({
  value,
  strong,
  unitPrice,
}: {
  value: number | null;
  strong?: boolean;
  // ед. цената НЕ се реже до 2 знака (formatUnitPrice) — сумите да
  unitPrice?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-end border-r border-shade-2 px-[11px] font-[ui-monospace,Menlo,monospace] text-[13px] ${
        value == null ? "text-ghost" : strong ? "font-semibold text-ink" : "text-body"
      }`}
    >
      {value == null ? "—" : unitPrice ? formatUnitPrice(value) : formatMoney(value)}
    </div>
  );
}

// ✕ за премахване на реда — както в работния лист на доставките: запълва
// клетката (или компактен 20px вариант, когато дели колоната с друга икона)
export function RemoveRowButton({ onClick, hint, compact }: { onClick: () => void; hint: string; compact?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      tabIndex={-1}
      className={`flex cursor-pointer items-center justify-center border-none bg-transparent text-sm text-ghost hover:text-danger-ink ${
        compact ? "h-5 w-5 shrink-0" : ""
      }`}
    >
      ✕
    </button>
  );
}
